// ===================
// 5TH CARD BONUS CALCULATION
// ===================
// Strategic evaluation for ending a round by playing a 5th card
//
// The 5th card bonus is ONLY calculated when:
// 1. The card being evaluated would be the 5th of its artist (currentPlayed === 4)
// 2. The AI is the auctioneer choosing which card to play
//
// This bonus is added to the card's EV to account for the strategic value
// of ending the round and locking in current positions.

import type { Artist, GameState, GameBoard, Player } from '../../../types/game'
import { ARTISTS, ARTIST_VALUES } from '../../../engine/constants'

// ============================================================================
// PUBLIC API - Called from pure-math-ev.ts
// ============================================================================

/**
 * Calculate the strategic bonus for ending the round by playing a 5th card.
 *
 * This should ONLY be called when evaluating a card that would be the 5th
 * of its artist (currentPlayed === 4).
 *
 * NEW FORMULA: myLead + (|netLoss| × probability_of_demotion)
 *
 * - myLead: what we gain by ending now (payout - max opponent payout)
 * - netLoss: potential loss if our winning artist gets demoted
 * - probability: based on unplayed cards of the threatening artist
 *
 * @param gameState - Current game state
 * @param playerIndex - AI player index
 * @returns EV bonus in thousands (0 if not advantageous to end round)
 */
export function calculateFifthCardBonus(
  gameState: GameState,
  playerIndex: number
): number {
  const myPayout = calculateMyPayoutIfRoundEnds(gameState, playerIndex)

  // If I have nothing to gain, no bonus
  if (myPayout === 0) return 0

  // Relative advantage: How much am I winning by vs opponents?
  // Benchmark against max opponent
  const maxOpponentPayout = getMaxOpponentPayout(gameState, playerIndex)

  // If we're actually losing (not tied), no bonus
  if (myPayout < maxOpponentPayout) return 0

  // myLead is 0 when tied, positive when winning
  const myLead = myPayout - maxOpponentPayout

  // Find the artist with 4 cards played (the one we'd end the round with)
  const fifthCardArtist = findFifthCardArtist(gameState)

  // DEFENSIVE: This function should only be called when a 5th card exists,
  // but if called incorrectly, we return just the lead value.
  // In practice, this should never happen because the caller checks
  // canEndRound() before calling this function.
  if (!fifthCardArtist) {
    return myLead
  }

  // Calculate net loss if this artist gets demoted
  const netLoss = calculateNetLossIfDemoted(
    gameState,
    playerIndex,
    fifthCardArtist
  )

  // Calculate probability of demotion based on unplayed cards of threatening artist
  const probability = calculateDemotionProbability(
    gameState,
    fifthCardArtist
  )

  // BONUS = myLead + (|netLoss| × probability)
  // This is the expected loss from NOT ending the round
  return myLead + (Math.abs(netLoss) * probability)
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate what player gets paid if round ends NOW.
 * Includes this round's payout (30/20/10/0) + cumulative value from previous rounds.
 */
function calculateMyPayoutIfRoundEnds(
  gameState: GameState,
  playerIndex: number
): number {
  const player = gameState.players[playerIndex]
  const board = gameState.board
  const currentRound = gameState.round.roundNumber

  let totalPayout = 0

  for (const painting of player.purchases || []) {
    const artist = painting.artist
    const rank = getArtistRank(board, artist)

    // This round's payout based on current rank
    const thisRoundPayout = getPayoutForRank(rank)

    // Cumulative value from all previous rounds (0 to currentRound-1)
    const cumulativeValue = board.artistValues[artist].reduce(
      (sum, value, roundIndex) => {
        return roundIndex < currentRound ? sum + value : sum
      },
      0
    )

    totalPayout += thisRoundPayout + cumulativeValue
  }

  return totalPayout
}

/**
 * Get max payout of all opponents if round ends now.
 * Only bonus if beating the strongest opponent.
 */
function getMaxOpponentPayout(
  gameState: GameState,
  playerIndex: number
): number {
  let maxPayout = 0

  for (let i = 0; i < gameState.players.length; i++) {
    if (i === playerIndex) continue

    const opponentPayout = calculateMyPayoutIfRoundEnds(gameState, i)
    maxPayout = Math.max(maxPayout, opponentPayout)
  }

  return maxPayout
}

/**
 * Find an artist that has 4 cards played (would be the 5th card).
 */
function findFifthCardArtist(gameState: GameState): Artist | null {
  const cardsPerArtist = gameState.round.cardsPlayedPerArtist

  for (const artist of ARTISTS) {
    if (cardsPerArtist[artist] === 4) {
      return artist
    }
  }

  return null
}

/**
 * Calculate the net loss if the specified artist gets demoted.
 *
 * If we're in 1st with Artist A and get demoted to 2nd:
 * - Loss: (1st_payout - 2nd_payout) × our_cards_of_A
 * - Gain: (1st_payout - 2nd_payout) × our_cards_of_B (if we own the 2nd place artist)
 *
 * netLoss = loss - gain (could be negative if we own more of the promoted artist)
 */
function calculateNetLossIfDemoted(
  gameState: GameState,
  playerIndex: number,
  artist: Artist
): number {
  const board = gameState.board
  const player = gameState.players[playerIndex]
  const currentRank = getArtistRank(board, artist)

  // Only care if we're in 1st or 2nd place (protecting valuable position)
  if (currentRank > 2) return 0

  // Find the artist that would overtake us (the one right behind)
  const threateningArtist = findThreateningArtist(board, artist)

  if (!threateningArtist) return 0

  // Calculate payout difference between current rank and next rank
  const currentPayout = getPayoutForRank(currentRank)
  const nextPayout = getPayoutForRank(currentRank + 1)
  const payoutDiff = currentPayout - nextPayout

  // Count how many cards we own of each artist
  const myCardsOfArtist = countCardsOwned(player, artist)
  const myCardsOfThreatening = countCardsOwned(player, threateningArtist)

  // Loss if our artist gets demoted
  const loss = payoutDiff * myCardsOfArtist

  // Gain if the threatening artist gets promoted
  const gain = payoutDiff * myCardsOfThreatening

  // Net loss = loss - gain
  // (if we own more of the threatening artist, this could be negative!)
  return loss - gain
}

/**
 * Find the artist that would overtake the given artist if they got one more card.
 */
function findThreateningArtist(board: GameBoard, artist: Artist): Artist | null {
  const myCount = board.playedCards[artist]?.length || 0
  const myRank = getArtistRank(board, artist)

  // Find the artist right behind us
  let threateningArtist: Artist | null = null
  let threateningCount = -1

  for (const otherArtist of ARTISTS) {
    if (otherArtist === artist) continue

    const otherCount = board.playedCards[otherArtist]?.length || 0
    const otherRank = getArtistRank(board, otherArtist)

    // This artist threatens us if they're right behind us in ranking
    if (otherRank === myRank + 1 && otherCount > threateningCount) {
      threateningArtist = otherArtist
      threateningCount = otherCount
    }
  }

  return threateningArtist
}

/**
 * Count how many cards of an artist the player owns.
 */
function countCardsOwned(player: Player, artist: Artist): number {
  if (!player.purchases) return 0

  return player.purchases.filter((p) => p.artist === artist).length
}

/**
 * Calculate the probability of demotion based on unplayed cards.
 *
 * Higher probability when:
 * - More unplayed cards of the threatening artist exist
 * - The threatening artist is close behind (1 card behind = high threat)
 *
 * PROBABILITY SCALING:
 * The base probability is: threateningUnplayed / totalUnplayed
 * This is then scaled by how many cards the threatening artist is behind:
 *
 * - 1 card behind: full probability (they could overtake with one play)
 * - 2 cards behind: ×0.5 (they need multiple favorable plays, still plausible)
 * - 3+ cards behind: ×0.2 (very unlikely, would require many favorable outcomes)
 *
 * Rationale for scaling factors:
 * - 0.5 for 2 cards behind: Assumes 50% chance they get to play 2 cards before round ends
 * - 0.2 for 3+ behind: Assumes 20% chance they get to play 3+ cards (highly unlikely)
 * These are heuristic estimates suitable for Medium AI difficulty.
 *
 * Returns a value between 0 and 1.
 */
function calculateDemotionProbability(
  gameState: GameState,
  artist: Artist
): number {
  const board = gameState.board
  const myCount = board.playedCards[artist]?.length || 0
  const currentRank = getArtistRank(board, artist)

  // Only relevant if we're in 1st or 2nd
  if (currentRank > 2) return 0

  const threateningArtist = findThreateningArtist(board, artist)
  if (!threateningArtist) return 0

  const threateningCount = board.playedCards[threateningArtist]?.length || 0

  // How many cards behind are they?
  const cardsBehind = myCount - threateningCount

  // If they're far behind, low probability
  // If they're 1 card behind, high probability

  // Count total unplayed cards
  const totalUnplayed = countTotalUnplayedCards(gameState)

  if (totalUnplayed === 0) return 0

  // Estimate unplayed cards of threatening artist
  // In a full game: 5 artists × 5 cards each = 25 cards per artist
  const TOTAL_CARDS_PER_ARTIST = 5
  const myPlayed = myCount
  const threateningPlayed = threateningCount

  // Unplayed cards of threatening artist (could be in deck or opponents' hands)
  const threateningUnplayed = TOTAL_CARDS_PER_ARTIST - threateningPlayed

  // Probability: fraction of unplayed cards that are of the threatening artist
  let probability = threateningUnplayed / totalUnplayed

  // Scale by how close they are:
  // - 1 card behind: full probability
  // - 2+ cards behind: reduced probability (need multiple plays)
  if (cardsBehind >= 2) {
    probability *= 0.5 // Need multiple favorable plays
  } else if (cardsBehind >= 3) {
    probability *= 0.2 // Very unlikely
  }

  // Clamp to [0, 1]
  return Math.max(0, Math.min(1, probability))
}

/**
 * Count total unplayed cards across all artists.
 */
function countTotalUnplayedCards(gameState: GameState): number {
  const deck = gameState.deck
  const players = gameState.players

  // Cards in deck
  let unplayedCount = deck.length

  // Cards in opponents' hands (estimate - we don't know what they have)
  for (let i = 0; i < players.length; i++) {
    unplayedCount += players[i].hand.length
  }

  return unplayedCount
}

/**
 * Get an artist's current rank (1-5) based on cards played.
 * Tie-breaker: board position (lower index = better).
 */
function getArtistRank(board: GameBoard, artist: Artist): number {
  const myCount = board.playedCards[artist]?.length || 0

  // Count how many artists have more cards (or same cards but better position)
  let rank = 1
  for (const otherArtist of ARTISTS) {
    if (otherArtist === artist) continue

    const otherCount = board.playedCards[otherArtist]?.length || 0

    // Other artist outranks me if:
    // - They have more cards, OR
    // - Same cards but better board position (lower index)
    if (otherCount > myCount) {
      rank++
    } else if (otherCount === myCount && ARTISTS.indexOf(otherArtist) < ARTISTS.indexOf(artist)) {
      rank++
    }
  }

  return rank
}

/**
 * Get payout value for a rank position using constants.
 */
function getPayoutForRank(rank: number): number {
  switch (rank) {
    case 1: return ARTIST_VALUES.FIRST
    case 2: return ARTIST_VALUES.SECOND
    case 3: return ARTIST_VALUES.THIRD
    default: return ARTIST_VALUES.FOURTH_OR_LOWER
  }
}
